import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNotificationSettings } from '@/hooks/useNotificationSettings';
import { useFontScale } from '@/contexts/FontScaleContext';
import Button from '@/components/base/Button';
import { useScrollLock } from '@/hooks/useScrollLock';

/**
 * 朝のリマインド通知（今日の予定ダイジェスト＋朝のタスク／日報などのリマインド）を
 * 各ユーザーが「初回に一度だけ」明示的に ON/OFF 選ぶための必須ポップアップ。
 *
 * - フォントサイズ設定（FontScalePrompt）と同系の実装。閉じる操作は用意しない。
 * - 文字サイズ未設定の間は出さない（2つの必須ポップアップが重ならないよう順番に出す）。
 * - 通知設定が未設定（morningReminder === null）のときだけ表示し、
 *   選んで保存すると自動で消える。後からは通知設定パネル（歯車）で変更できる。
 */
export default function MorningReminderPrompt() {
  const { user, loading } = useAuth();
  const { configured: fontScaleConfigured } = useFontScale();
  const { loaded, morningReminderConfigured, setMorningReminder } = useNotificationSettings(
    user?.id
  );
  const [selected, setSelected] = useState<boolean | null>(null);
  const [showHint, setShowHint] = useState(false);

  const open =
    !loading && !!user && fontScaleConfigured && loaded && !morningReminderConfigured;

  // 表示中は背後のスクロールを止める（html / body 両方をロック）
  useScrollLock(open);

  if (!open) return null;

  const handleConfirm = () => {
    if (selected === null) {
      setShowHint(true);
      return;
    }
    // 保存すると configured が true になり自動で閉じる
    setMorningReminder(selected);
  };

  const options: Array<{
    value: boolean;
    label: string;
    icon: string;
    description: string;
  }> = [
    {
      value: true,
      label: '受け取る',
      icon: 'ri-notification-3-line',
      description:
        '毎朝、その日の予定やタスク・日報のリマインドをまとめて通知します。',
    },
    {
      value: false,
      label: '受け取らない',
      icon: 'ri-notification-off-line',
      description: '朝のリマインド通知は送りません。あとからでも変更できます。',
    },
  ];

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
        <div className="px-5 sm:px-6 py-5 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center text-white shrink-0"
              style={{ backgroundColor: 'var(--theme-primary)' }}
            >
              <i className="ri-sun-line text-lg"></i>
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-gray-900">朝のリマインド通知</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                通知を受け取るかどうかを選んでください。
              </p>
            </div>
          </div>
        </div>

        <div className="px-5 sm:px-6 py-5 overflow-y-auto flex-1">
          <p className="text-sm text-gray-600 mb-4">
            毎朝、その日の予定（今日の予定ダイジェスト）やタスクなどのリマインドを通知でお知らせします。ここで選んだ内容はあとから通知設定（ベルの中の歯車）でいつでも変更できます。
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {options.map((option) => {
              const active = selected === option.value;
              return (
                <button
                  key={String(option.value)}
                  type="button"
                  onClick={() => {
                    setSelected(option.value);
                    setShowHint(false);
                  }}
                  aria-pressed={active}
                  className={`text-left rounded-lg border p-4 transition-colors cursor-pointer ${
                    active
                      ? 'border-[color:var(--theme-primary)] bg-[color:var(--theme-primary-light)] ring-2 ring-[color:var(--theme-primary)]'
                      : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span
                      className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${
                        active
                          ? 'bg-[color:var(--theme-primary)] text-white'
                          : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      <i className={`${option.icon} text-base`}></i>
                    </span>
                    <span
                      className={`text-sm font-bold ${
                        active ? 'text-[color:var(--theme-primary)]' : 'text-gray-900'
                      }`}
                    >
                      {option.label}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    {option.description}
                  </p>
                </button>
              );
            })}
          </div>

          {showHint && selected === null && (
            <p className="mt-3 text-sm text-red-500">どちらかを選んでください。</p>
          )}
        </div>

        <div className="px-5 sm:px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end shrink-0">
          <Button variant="primary" onClick={handleConfirm}>
            この内容で始める
          </Button>
        </div>
      </div>
    </div>
  );
}